import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { activateAccount, verifyActivationToken } from '../../../services/api/strapiAuth';
import { useAuth } from '../../../contexts/AuthContext';
import './AccountActivationPage.scss';
import SvgIcons from '../../../services/SvgIcons';
import { checkCapital, checkDigit, checkLowercase, checkSpecialCharacter } from '../../../services/inputCheck';
import Loading from '../../../components/Loading/Loading';

/**
 * Page d'activation de compte après paiement System.io
 * Permet à l'utilisateur de créer son mot de passe et activer son compte
 */
const AccountActivationPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { updateUser } = useAuth();

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [tokenValid, setTokenValid] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [hasGoodLength, setHasGoodLength] = useState(false);
    const [hasCapitalLetter, setHasCapitalLetter] = useState(false);
    const [hasLowercaseLetter, setHasLowercaseLetter] = useState(false);
    const [hasDigit, setHasDigit] = useState(false);
    const [hasSpecialCharacter, setHasSpecialCharacter] = useState(false);
    const [passwordsMatch, setPasswordsMatch] = useState(false);
    const [enabledValidateBtn, setEnabledValidateBtn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate('/');
            return;
        }

        // Vérifier la validité du token au chargement
        verifyToken();
    }, [token]);

    const verifyToken = async () => {
        try {
            const response = await verifyActivationToken(token);

            if (response.success) {
                setTokenValid(true);
                setUserInfo(response.user);
            } else {
                setTokenValid(false);
            }
        } catch (error) {
            console.error('Erreur vérification token:', error);
            setTokenValid(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Validation en temps réel
        if (name === 'password') {
            e.target.value.length >= 8 && e.target.value.length <= 20 ? setHasGoodLength(true) : setHasGoodLength(false);
            checkCapital(e.target.value) ? setHasCapitalLetter(true) : setHasCapitalLetter(false);
            checkLowercase(e.target.value) ? setHasLowercaseLetter(true) : setHasLowercaseLetter(false);
            checkDigit(e.target.value) ? setHasDigit(true) : setHasDigit(false);
            checkSpecialCharacter(e.target.value) ? setHasSpecialCharacter(true) : setHasSpecialCharacter(false);

            // Vérifier concordance si confirmPassword existe
            if (formData.confirmPassword) {
                e.target.value === formData.confirmPassword ? setPasswordsMatch(true) : setPasswordsMatch(false);
            }
        }

        if (name === 'confirmPassword') {
            e.target.value === formData.password ? setPasswordsMatch(true) : setPasswordsMatch(false);
        }

        // Effacer l'erreur du champ modifié
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };


    // Vérifier si le bouton doit être activé
    useEffect(() => {
        const isValid = hasGoodLength &&
            hasCapitalLetter &&
            hasLowercaseLetter &&
            hasDigit &&
            hasSpecialCharacter &&
            passwordsMatch &&
            formData.password.length > 0 &&
            formData.confirmPassword.length > 0;

        setEnabledValidateBtn(isValid);
    }, [hasGoodLength, hasCapitalLetter, hasLowercaseLetter, hasDigit, hasSpecialCharacter, passwordsMatch, formData.password, formData.confirmPassword]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };



    const validateForm = () => {
        const newErrors = {};

        if (!formData.password) {
            newErrors.password = 'Le mot de passe est requis';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
        } else if (formData.password.length > 20) {
            newErrors.password = 'Le mot de passe ne doit pas dépasser 20 caractères';
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await activateAccount(token, formData.password);

            if (response.success) {
                // Mettre à jour le contexte auth avec le profil complet (incluant les formations)
                await updateUser();

                // Redirection vers les formations
                navigate('/mes-formations');
            }
        } catch (error) {
            setErrors({
                submit: error.message || 'Une erreur est survenue. Veuillez réessayer.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (tokenValid === false) {
        return (
            <Loading />
        );
    }

    if (tokenValid === false) {
        return (
            <div className="activation-page">
                <div className="logo_container">
                    <picture>
                        <source srcSet={'/assets/logo_transparent.webp'} type="image/webp" />
                        <img src={'/assets/logo_transparent.png'} alt="Logo Grandir et s'élever ensembl" loading="eager" fetchpriority="high" />
                    </picture>
                </div>
                <div className="activation-page__container">
                    <div className="activation-page__error">
                        <h1>Lien invalide ou expiré</h1>
                        <p>Ce lien d'activation n'est plus valide.</p>
                        <p>S'il s'agit d'une erreur, contacte moi ici :</p>
                        <a href="mailto:melaniebouthors@gmail.com">melaniebouthors@gmail.com</a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="activation-page">
            <div className="logo_container">
                <picture>
                    <source srcSet={'/assets/logo_transparent.webp'} type="image/webp" />
                    <img src={'/assets/logo_transparent.png'} alt="Logo Grandir et s'élever ensembl" loading="eager" fetchpriority="high" />
                </picture>
            </div>
            <div className="activation-page__container">
                <div className="activation-page__header">
                    <h1>Bienvenue &nbsp; {userInfo?.firstname} !</h1>
                    <p>Créez votre mot de passe pour accéder à votre formation</p>
                </div>

                <div className='create-password-form__rules_container'>
                    <div className='create-password-form__rule_container'>
                        <SvgIcons
                            name={hasGoodLength ? 'check_circle_fill' : 'empty_circle'}
                            width="20"
                            height="20"
                            color={hasGoodLength ? "#096e48" : "#36152F"}
                        />
                        <p className='create-password-form__rules_require'>Le mot de passe doit contenir entre 8 et 20 caractères</p>
                    </div>
                    <div className='create-password-form__rule_container'>
                        <SvgIcons
                            name={hasLowercaseLetter ? 'check_circle_fill' : 'empty_circle'}
                            width="20"
                            height="20"
                            color={hasLowercaseLetter ? "#096e48" : "#36152F"}
                        />
                        <p className='create-password-form__rules_require'>Le mot de passe doit contenir au moins une lettre minuscule</p>
                    </div>
                    <div className='create-password-form__rule_container'>
                        <SvgIcons
                            name={hasCapitalLetter ? 'check_circle_fill' : 'empty_circle'}
                            width="20"
                            height="20"
                            color={hasCapitalLetter ? "#096e48" : "#36152F"}
                        />
                        <p className='create-password-form__rules_require'>Le mot de passe doit contenir au moins une lettre majuscule</p>
                    </div>
                    <div className='create-password-form__rule_container'>
                        <SvgIcons
                            name={hasDigit ? 'check_circle_fill' : 'empty_circle'}
                            width="20"
                            height="20"
                            color={hasDigit ? "#096e48" : "#36152F"}
                        />
                        <p className='create-password-form__rules_require'>Le mot de passe doit contenir au moins un chiffre</p>
                    </div>
                    <div className='create-password-form__rule_container'>
                        <SvgIcons
                            name={hasSpecialCharacter ? 'check_circle_fill' : 'empty_circle'}
                            width="20"
                            height="20"
                            color={hasSpecialCharacter ? "#096e48" : "#36152F"}
                        />
                        <p className='create-password-form__rules_require'>Le mot de passe doit contenir au moins un caractère spécial</p>
                    </div>
                    <div className='create-password-form__rule_container'>
                        <SvgIcons
                            name={passwordsMatch ? 'check_circle_fill' : 'empty_circle'}
                            width="20"
                            height="20"
                            color={passwordsMatch ? "#096e48" : "#36152F"}
                        />
                        <p className='create-password-form__rules_require'>Les mots de passe sont identiques</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="activation-page__form">
                    {errors.submit && (
                        <div className="activation-page__error-message">
                            {errors.submit}
                        </div>
                    )}

                    <div className="activation-page__form-group">
                        <label htmlFor="password">Mot de passe</label>
                        <div className="create-password-form__input-container">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={errors.password ? 'error' : ''}
                                placeholder="Minimum 8 caractères"
                            />
                            <button
                                type="button"
                                tabIndex="-1"
                                onClick={togglePasswordVisibility}
                                className="activation-page__toggle-password"
                                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                            >
                                <SvgIcons
                                    name={showPassword ? 'eye' : 'eye-off'}
                                    width="24"
                                    height="24"
                                    color="#36152F"
                                />
                            </button>
                        </div>
                        {errors.password && (
                            <span className="activation-page__field-error">{errors.password}</span>
                        )}
                    </div>

                    <div className="activation-page__form-group">
                        <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                        <div className="create-password-form__input-container">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className={errors.confirmPassword ? 'error' : ''}
                                placeholder="Retapez votre mot de passe"
                            />
                            <button
                                type="button"
                                tabIndex="-1"
                                onClick={toggleConfirmPasswordVisibility}
                                className="activation-page__toggle-password"
                                aria-label={showConfirmPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                            >
                                <SvgIcons
                                    name={showConfirmPassword ? 'eye' : 'eye-off'}
                                    width="24"
                                    height="24"
                                    color="#36152F"
                                />
                            </button>
                        </div>
                        {errors.confirmPassword && (
                            <span className="activation-page__field-error">{errors.confirmPassword}</span>
                        )}
                    </div>

                    <button 
                        type="submit" 
                        className="activation-page__submit"
                        disabled={isLoading || !enabledValidateBtn}
                    >
                        {isLoading ? 'Activation en cours...' : 'Activer mon compte'}
                    </button>
                </form>

                <div className="activation-page__info">
                    <p>Une fois votre compte activé, vous aurez accès immédiatement à votre formation.</p>
                </div>
            </div>
        </div>
    );
};

export default AccountActivationPage;